(function () {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shopRoot = window.Shopify?.routes?.root || '/';
  const shopRoute = (path) => `${shopRoot.replace(/\/?$/, '/')}${String(path).replace(/^\//, '')}`;

  const markReady = (element, name) => {
    if (!element || element.dataset[name] === 'true') return false;
    element.dataset[name] = 'true';
    return true;
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-as-toast]');
    if (!toast) return;
    const content = toast.firstElementChild || toast;
    content.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(Number(toast.dataset.hideTimer || 0));
    toast.dataset.hideTimer = String(window.setTimeout(() => toast.classList.remove('is-visible'), 4200));
  };

  const initDrawer = (scope) => {
    scope.querySelectorAll('[data-as-mobile-drawer]').forEach((drawer) => {
      if (!markReady(drawer, 'drawerReady')) return;
      const open = document.querySelector('[data-as-menu-open]');
      const close = drawer.querySelector('[data-as-menu-close]');
      const setDrawer = (state) => {
        drawer.classList.toggle('is-open', state);
        drawer.setAttribute('aria-hidden', state ? 'false' : 'true');
        document.documentElement.style.overflow = state ? 'hidden' : '';
        open?.setAttribute('aria-expanded', state ? 'true' : 'false');
      };
      open?.addEventListener('click', () => setDrawer(true));
      close?.addEventListener('click', () => setDrawer(false));
      drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setDrawer(false)));
      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) setDrawer(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) setDrawer(false);
      });
    });
  };

  const initHero = (scope) => {
    scope.querySelectorAll('[data-as-hero]').forEach((hero) => {
      if (!markReady(hero, 'heroReady')) return;

      const product = hero.querySelector('[data-as-hero-product]');
      if (product && window.matchMedia('(pointer:fine)').matches && !reducedMotion()) {
        let targetX = 0;
        let targetY = 0;
        let currentX = 0;
        let currentY = 0;
        let animationFrame = 0;
        const render = () => {
          currentX += (targetX - currentX) * 0.075;
          currentY += (targetY - currentY) * 0.075;
          product.style.setProperty('--as-x', `${currentX * 14}px`);
          product.style.setProperty('--as-y', `${currentY * 10}px`);
          product.style.setProperty('--as-r', `${currentX * 1.4}deg`);
          animationFrame = window.requestAnimationFrame(render);
        };
        hero.addEventListener('pointermove', (event) => {
          const rect = hero.getBoundingClientRect();
          targetX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
          targetY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        });
        hero.addEventListener('pointerleave', () => {
          targetX = 0;
          targetY = 0;
        });
        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !animationFrame) render();
          if (!entry.isIntersecting && animationFrame) {
            window.cancelAnimationFrame(animationFrame);
            animationFrame = 0;
          }
        });
        observer.observe(hero);
      }

      const video = hero.querySelector('.as-hero__video');
      if (!video) return;
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;

      const controls = hero.querySelector('[data-as-video-controls]');
      const toggle = controls?.querySelector('[data-as-video-toggle]');
      const mute = controls?.querySelector('[data-as-video-mute]');
      const progress = controls?.querySelector('[data-as-video-progress]');
      const current = controls?.querySelector('[data-as-video-current]');
      const duration = controls?.querySelector('[data-as-video-duration]');
      const formatTime = (seconds) => {
        if (!Number.isFinite(seconds)) return '0:00';
        return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
      };
      const play = () => video.play().catch(() => {});
      const sync = () => {
        controls?.classList.toggle('is-paused', video.paused);
        controls?.classList.toggle('is-muted', video.muted);
        toggle?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        mute?.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Mute video');
        if (current) current.textContent = formatTime(video.currentTime);
        if (duration) duration.textContent = formatTime(video.duration);
        if (progress && document.activeElement !== progress && Number.isFinite(video.duration) && video.duration > 0) {
          progress.value = String(Math.round((video.currentTime / video.duration) * 1000));
        }
      };

      toggle?.addEventListener('click', () => {
        if (video.paused) play();
        else video.pause();
      });
      mute?.addEventListener('click', () => {
        video.muted = !video.muted;
        sync();
      });
      progress?.addEventListener('input', () => {
        if (Number.isFinite(video.duration) && video.duration > 0) {
          video.currentTime = (Number(progress.value) / 1000) * video.duration;
        }
      });
      ['play', 'pause', 'volumechange', 'loadedmetadata', 'durationchange', 'timeupdate', 'ended'].forEach((name) => {
        video.addEventListener(name, sync);
      });

      if (!reducedMotion()) {
        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) play();
          else video.pause();
        }, { threshold: 0.05 });
        observer.observe(video);
        ['loadedmetadata', 'loadeddata', 'canplay'].forEach((name) => video.addEventListener(name, play, { once: true }));
        if (video.readyState >= 2) window.requestAnimationFrame(play);
      }
      sync();
    });
  };

  const initVariants = (scope) => {
    scope.querySelectorAll('[data-as-product-root]').forEach((productRoot) => {
      if (!markReady(productRoot, 'variantReady')) return;
      const select = productRoot.querySelector('[data-as-variant-select]');
      if (!select) return;
      const sync = () => {
        const option = select.options[select.selectedIndex];
        productRoot.querySelectorAll('[name="id"]').forEach((input) => { input.value = select.value; });
        productRoot.querySelectorAll('[data-as-price]').forEach((element) => {
          element.textContent = option.dataset.price || '';
        });
        productRoot.querySelectorAll('[data-as-atc]').forEach((button) => {
          const available = option.dataset.available === 'true';
          button.disabled = !available;
          button.textContent = available ? button.dataset.availableLabel : button.dataset.soldLabel;
        });
      };
      select.addEventListener('change', sync);
      sync();
    });
  };

  const initStickyCart = (scope) => {
    scope.querySelectorAll('[data-as-sticky-atc]').forEach((sticky) => {
      if (!markReady(sticky, 'stickyReady')) return;
      const root = sticky.closest('[data-as-product-root]') || document;
      const mainButton = root.querySelector('[data-as-main-atc]');
      if (!mainButton) return;
      const observer = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 });
      observer.observe(mainButton);
    });
  };

  const initGalleries = (scope) => {
    scope.querySelectorAll('[data-as-gallery]').forEach((gallery) => {
      if (!markReady(gallery, 'galleryReady')) return;
      const track = gallery.querySelector('[data-as-gallery-track]');
      const items = Array.from(track?.querySelectorAll('[data-as-gallery-item]') || []);
      if (!track || !items.length) return;

      const previous = gallery.querySelector('[data-as-gallery-prev]');
      const next = gallery.querySelector('[data-as-gallery-next]');
      const controls = gallery.querySelector('[data-as-gallery-controls]');
      const status = gallery.querySelector('[data-as-gallery-status]');
      let current = 0;
      let scrollFrame = 0;
      const update = () => {
        if (controls) controls.hidden = items.length < 2;
        if (status) status.textContent = `${current + 1} / ${items.length}`;
        previous?.toggleAttribute('disabled', items.length < 2);
        next?.toggleAttribute('disabled', items.length < 2);
        items.forEach((item, index) => {
          if (index !== current) item.querySelectorAll('video').forEach((video) => video.pause());
        });
      };
      const goTo = (index) => {
        current = (index + items.length) % items.length;
        track.scrollTo({
          left: items[current].offsetLeft,
          behavior: reducedMotion() ? 'auto' : 'smooth'
        });
        update();
      };
      previous?.addEventListener('click', () => goTo(current - 1));
      next?.addEventListener('click', () => goTo(current + 1));
      track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          const nearest = items.reduce((best, item, index) => {
            const distance = Math.abs(item.offsetLeft - track.scrollLeft);
            return distance < best.distance ? { index, distance } : best;
          }, { index: current, distance: Number.POSITIVE_INFINITY });
          current = nearest.index;
          update();
        });
      }, { passive: true });

      const zoomable = items.filter((item) => item.querySelector('img'));
      if (!zoomable.length) {
        update();
        return;
      }

      const modal = document.createElement('dialog');
      modal.className = 'as-gallery-lightbox';
      modal.setAttribute('aria-label', 'Expanded product image');
      modal.innerHTML = `
        <button class="as-gallery-lightbox__close" type="button" aria-label="Close expanded image">×</button>
        <button class="as-gallery-lightbox__prev" type="button" aria-label="Previous expanded image">←</button>
        <figure><img alt=""><figcaption></figcaption></figure>
        <button class="as-gallery-lightbox__next" type="button" aria-label="Next expanded image">→</button>
      `;
      document.body.appendChild(modal);
      let zoomIndex = 0;
      const bestSource = (image) => {
        const sources = (image.getAttribute('srcset') || '')
          .split(',')
          .map((entry) => entry.trim().split(/\s+/)[0])
          .filter(Boolean);
        return sources[sources.length - 1] || image.currentSrc || image.src;
      };
      const renderZoom = () => {
        const source = zoomable[zoomIndex]?.querySelector('img');
        const image = modal.querySelector('img');
        if (!source || !image) return;
        image.src = bestSource(source);
        image.alt = source.alt || '';
        modal.querySelector('figcaption').textContent = source.alt || '';
      };
      const openZoom = (item) => {
        zoomIndex = Math.max(0, zoomable.indexOf(item));
        renderZoom();
        if (typeof modal.showModal === 'function') modal.showModal();
        else modal.setAttribute('open', '');
      };
      const closeZoom = () => {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
      };
      zoomable.forEach((item) => {
        item.classList.add('is-zoomable');
        item.tabIndex = 0;
        item.setAttribute('role', 'button');
        item.setAttribute('aria-label', 'Open expanded product image');
        item.addEventListener('click', () => openZoom(item));
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openZoom(item);
          }
        });
      });
      modal.querySelector('.as-gallery-lightbox__close')?.addEventListener('click', closeZoom);
      modal.querySelector('.as-gallery-lightbox__prev')?.addEventListener('click', () => {
        zoomIndex = (zoomIndex - 1 + zoomable.length) % zoomable.length;
        renderZoom();
      });
      modal.querySelector('.as-gallery-lightbox__next')?.addEventListener('click', () => {
        zoomIndex = (zoomIndex + 1) % zoomable.length;
        renderZoom();
      });
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeZoom();
      });
      modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeZoom();
        if (event.key === 'ArrowLeft') {
          zoomIndex = (zoomIndex - 1 + zoomable.length) % zoomable.length;
          renderZoom();
        }
        if (event.key === 'ArrowRight') {
          zoomIndex = (zoomIndex + 1) % zoomable.length;
          renderZoom();
        }
      });
      update();
    });
  };

  const initReviews = (scope) => {
    scope.querySelectorAll('[data-as-reviews]').forEach((reviews) => {
      if (!markReady(reviews, 'reviewsReady')) return;
      const track = reviews.querySelector('[data-as-reviews-track]');
      const originals = Array.from(reviews.querySelectorAll('[data-as-review-card]'));
      const dots = Array.from(reviews.querySelectorAll('[data-as-review-dot]'));
      if (!track || originals.length < 2) return;

      const count = originals.length;
      const interval = Math.max(3000, Number(reviews.dataset.interval) || 6000);
      const clone = (card) => {
        const copy = card.cloneNode(true);
        copy.dataset.reviewClone = 'true';
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('id');
        copy.removeAttribute('data-shopify-editor-block');
        copy.removeAttribute('data-shopify-editor-block-id');
        copy.removeAttribute('data-shopify-editor-block-type');
        return copy;
      };
      originals.forEach((card, index) => { card.dataset.reviewIndex = String(index); });
      track.prepend(...originals.map(clone));
      track.append(...originals.map(clone));
      const cards = Array.from(track.querySelectorAll('[data-as-review-card]'));
      let activeAllIndex = count;
      let autoplayTimer = 0;
      let settleTimer = 0;
      let dragging = false;
      let dragStartX = 0;
      let dragStartScroll = 0;
      let dragMoved = false;

      const logicalIndex = (index) => ((index % count) + count) % count;
      const centerLeft = (card) => card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
      const nearestIndex = () => {
        const center = track.getBoundingClientRect().left + track.clientWidth / 2;
        return cards.reduce((best, card, index) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: activeAllIndex, distance: Number.POSITIVE_INFINITY }).index;
      };
      const paint = (index) => {
        activeAllIndex = index;
        const logical = logicalIndex(index);
        cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
        dots.forEach((dot, dotIndex) => {
          dot.setAttribute('aria-current', dotIndex === logical ? 'true' : 'false');
          dot.tabIndex = dotIndex === logical ? 0 : -1;
        });
      };
      const move = (index, behavior = 'smooth') => {
        const target = Math.max(0, Math.min(cards.length - 1, index));
        paint(target);
        track.scrollTo({ left: centerLeft(cards[target]), behavior: reducedMotion() ? 'auto' : behavior });
      };
      const normalize = () => {
        if (dragging) return;
        const nearest = nearestIndex();
        const normalized = nearest < count ? nearest + count : nearest >= count * 2 ? nearest - count : nearest;
        if (normalized !== nearest) {
          track.scrollTo({ left: centerLeft(cards[normalized]), behavior: 'auto' });
        }
        paint(normalized);
      };
      const scheduleNormalize = (delay = 500) => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(normalize, delay);
      };
      const restartAutoplay = () => {
        window.clearInterval(autoplayTimer);
        if (reviews.dataset.autoplay !== 'true' || reducedMotion()) return;
        autoplayTimer = window.setInterval(() => {
          if (!document.hidden && !reviews.matches(':hover') && !reviews.contains(document.activeElement)) {
            move(nearestIndex() + 1);
            scheduleNormalize(650);
          }
        }, interval);
      };

      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        move(count + index);
        scheduleNormalize();
        restartAutoplay();
      }));
      track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(nearestIndex() + (event.key === 'ArrowRight' ? 1 : -1));
        scheduleNormalize();
        restartAutoplay();
      });
      track.addEventListener('pointerdown', (event) => {
        dragging = true;
        dragMoved = false;
        dragStartX = event.clientX;
        dragStartScroll = track.scrollLeft;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
        window.clearInterval(autoplayTimer);
        window.clearTimeout(settleTimer);
      });
      track.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const delta = event.clientX - dragStartX;
        if (Math.abs(delta) > 5) dragMoved = true;
        track.scrollLeft = dragStartScroll - delta;
      });
      const finishDrag = (event) => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        track.releasePointerCapture?.(event.pointerId);
        move(nearestIndex());
        scheduleNormalize();
        restartAutoplay();
      };
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener('click', (event) => {
        if (!dragMoved) return;
        event.preventDefault();
        event.stopPropagation();
        dragMoved = false;
      }, true);
      track.addEventListener('scroll', () => {
        if (dragging) return;
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          paint(nearestIndex());
          normalize();
        }, 120);
      }, { passive: true });
      reviews.addEventListener('mouseenter', () => window.clearInterval(autoplayTimer));
      reviews.addEventListener('mouseleave', restartAutoplay);
      reviews.addEventListener('focusin', () => window.clearInterval(autoplayTimer));
      reviews.addEventListener('focusout', restartAutoplay);

      window.requestAnimationFrame(() => {
        move(count, 'auto');
        normalize();
        restartAutoplay();
      });
    });
  };

  const initReviewHubs = (scope) => {
    scope.querySelectorAll('[data-as-review-hub]').forEach((hub) => {
      if (!markReady(hub, 'reviewHubReady')) return;
      const tabs = Array.from(hub.querySelectorAll('[data-as-review-tab]'));
      const panels = Array.from(hub.querySelectorAll('[data-as-review-panel]'));
      if (!tabs.length || !panels.length) return;
      const activate = (name, focus = false) => {
        tabs.forEach((tab) => {
          const active = tab.dataset.asReviewTab === name;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        panels.forEach((panel) => { panel.hidden = panel.dataset.asReviewPanel !== name; });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab.dataset.asReviewTab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next].dataset.asReviewTab, true);
        });
      });
      activate((tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]).dataset.asReviewTab);
    });
  };

  const initSort = (scope) => {
    scope.querySelectorAll('[data-as-sort]').forEach((select) => {
      if (!markReady(select, 'sortReady')) return;
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const initScope = (scope = document) => {
    initDrawer(scope);
    initHero(scope);
    initVariants(scope);
    initStickyCart(scope);
    initGalleries(scope);
    initReviews(scope);
    initReviewHubs(scope);
    initSort(scope);
  };

  const bindGlobalCart = () => {
    if (document.documentElement.dataset.asCartReady === 'true') return;
    document.documentElement.dataset.asCartReady = 'true';
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-as-ajax-cart]');
      if (!form || !window.fetch) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const original = button?.textContent || '';
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }
      try {
        const response = await window.fetch(shopRoute('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
        const cart = await window.fetch(shopRoute('cart.js')).then((cartResponse) => cartResponse.json());
        document.querySelectorAll('[data-as-cart-count]').forEach((element) => {
          element.textContent = String(cart.item_count);
        });
        showToast(`${result.product_title || 'Item'} added to your bag.`);
      } catch (error) {
        showToast(error.message || 'Something went wrong. Please try again.');
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = original;
        }
      }
    });
  };

  ready(() => {
    bindGlobalCart();
    initScope(document);
    document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  });
})();
